import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Users, User, Trophy, Info, Tag, Check, X, Loader2 } from 'lucide-react';

import { useIsMobile } from "@/hooks/use-mobile";
import { FriendSelector } from './FriendSelector';
import { useUserProfile } from '@/hooks/useUserProfile';
import { useAllSubcategoryCounts } from "@/hooks/useAllSubcategoryCounts";
import { getSubcategoryMaxParticipants } from "@/utils/eventUtils";
import { useLanguage } from '@/contexts/LanguageContext';
import { supabase } from '@/integrations/supabase/client';

interface RegistrationDataStepProps {
  event: any;
  data: any;
  onDataChange: (data: any) => void;
  onNext: () => void;
  onPrevious: (() => void) | null;
  isInvitationFlow?: boolean;
}

export const RegistrationDataStep = ({ event, data, onDataChange, onNext, onPrevious, isInvitationFlow = false }: RegistrationDataStepProps) => {
  const isMobile = useIsMobile();
  const { profile } = useUserProfile();
  const { t, language } = useLanguage();

  const translateCategory = (cat: string): string => {
    if (language === 'es') return cat;
    const map: Record<string, string> = {
      'masculino': "Men's", 'femenino': "Women's", 'mixto': 'Mixed',
    };
    return map[cat.toLowerCase()] || cat;
  };

  const translateSubcategory = (sub: string): string => {
    if (language === 'es') return sub;
    const map: Record<string, string> = {
      '1era': 'Division 1', '2nda': 'Division 2', '3era': 'Division 3',
      '4ta': 'Division 4', '5ta': 'Division 5', '6ta': 'Division 6',
    };
    return map[sub.toLowerCase()] || sub;
  };
  const [selectedModality, setSelectedModality] = useState(data.modality || '');
  const [selectedCategory, setSelectedCategory] = useState(data.category || '');
  const [selectedSubcategory, setSelectedSubcategory] = useState(data.subcategory || '');
  const [player1Name, setPlayer1Name] = useState(data.player1Name || profile?.full_name || '');
  const [player2Name, setPlayer2Name] = useState(data.player2Name || '');
  
  // New partner selection states
  const [partnerSelectionMethod, setPartnerSelectionMethod] = useState(data.partnerSelectionMethod || '');
  const [selectedFriend, setSelectedFriend] = useState(data.selectedFriend || null);
  
  // Promo code states
  const [promoCode, setPromoCode] = useState(data.promoCode || '');
  const [promoDiscount, setPromoDiscount] = useState<number | null>(data.discountPercent || null);
  const [promoError, setPromoError] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoValidated, setPromoValidated] = useState(!!data.promoCode && !!data.discountPercent);
  const [hasPromoCodes, setHasPromoCodes] = useState(false);

  useEffect(() => {
    const checkPromoCodes = async () => {
      const { count } = await supabase
        .from('ambassador_codes')
        .select('id', { count: 'exact', head: true })
        .eq('event_id', event.id)
        .eq('is_active', true);
      setHasPromoCodes((count || 0) > 0);
    };
    if (event.id) checkPromoCodes();
  }, [event.id]);

  const isLeague = event.event_type === 'league';
  
  // Determine available modalities
  const getAvailableModalities = () => {
    if (event.sport?.toLowerCase().includes('pádel') || event.sport?.toLowerCase().includes('padel')) {
      if (event.event_type === 'league') {
        return event.match_type === 'ambos' ? ['singles', 'dobles'] : [event.match_type];
      }
      return ['dobles']; // Padel tournaments are always doubles
    }
    return event.match_type === 'ambos' ? ['singles', 'dobles'] : [event.match_type];
  };

  const availableModalities = getAvailableModalities();
  const isDoublesRequired = selectedModality === 'dobles';

  // Auto-select modality when only one is available
  useEffect(() => {
    if (availableModalities.length === 1 && !selectedModality) {
      setSelectedModality(availableModalities[0]);
    }
  }, [availableModalities, selectedModality]);

  // Auto-select category when only one is available (leagues typically have 1 category)
  useEffect(() => {
    if (event.genderCategories?.length === 1 && !selectedCategory) {
      setSelectedCategory(event.genderCategories[0]);
    } else if (event.gender_category && (!event.genderCategories || event.genderCategories.length === 0) && !selectedCategory) {
      setSelectedCategory(event.gender_category);
    }
  }, [event.genderCategories, event.gender_category, selectedCategory]);

  // Get available subcategories based on selected category
  const getAvailableSubcategories = () => {
    if (!selectedCategory || !event.subcategories) return [];
    return event.subcategories[selectedCategory] || [];
  };

  const availableSubcategories = getAvailableSubcategories();

  // Get participant counts for ALL subcategories at once
  const { counts: subcategoryCounts, loading: allSubcategoriesLoading } = useAllSubcategoryCounts(
    event.id.toString(),
    selectedCategory,
    availableSubcategories,
    event.rules
  );

  // Get counts for currently selected subcategory (for validation)
  const selectedSubcategoryInfo = subcategoryCounts[selectedSubcategory];
  const pairsCount = selectedSubcategoryInfo?.pairsCount || 0;
  const subcategoryMax = selectedSubcategoryInfo?.max || null;
  const remainingPairs = selectedSubcategoryInfo?.remaining || null;

  // Handle category change and auto-select subcategory
  useEffect(() => {
    if (availableSubcategories.length === 1) {
      setSelectedSubcategory(availableSubcategories[0]);
    } else if (availableSubcategories.length === 0 || !availableSubcategories.includes(selectedSubcategory)) {
      setSelectedSubcategory('');
    }
  }, [selectedCategory, availableSubcategories]);

  // Initialize form data from props
  useEffect(() => {
    if (data.modality) setSelectedModality(data.modality);
    if (data.category) setSelectedCategory(data.category);
    if (data.subcategory) setSelectedSubcategory(data.subcategory);
    if (data.player1Name) setPlayer1Name(data.player1Name);
    if (data.player2Name) setPlayer2Name(data.player2Name);
    if (data.partnerSelectionMethod) setPartnerSelectionMethod(data.partnerSelectionMethod);
    if (data.selectedFriend) setSelectedFriend(data.selectedFriend);
  }, [data]);

  // Auto-fill player1Name with user's full name when profile loads
  useEffect(() => {
    if (profile?.full_name && !data.player1Name && !player1Name) {
      setPlayer1Name(profile.full_name);
    }
  }, [profile?.full_name, data.player1Name, player1Name]);

  // Handle form validation
  const isFormValid = () => {
    if (!selectedModality || !selectedCategory) return false;
    if (!player1Name.trim()) return false;
    if (subcategoryMax !== null && pairsCount >= subcategoryMax) return false;
    if (isDoublesRequired) {
      if (!partnerSelectionMethod) return false;
      if (partnerSelectionMethod === 'manual') return player2Name.trim() !== '';
      if (partnerSelectionMethod === 'friend') return selectedFriend !== null;
    }
    return true;
  };

  const validatePromoCode = async () => {
    if (!promoCode.trim()) return;
    setPromoLoading(true);
    setPromoError('');
    try {
      const { data: codes, error } = await supabase
        .from('ambassador_codes')
        .select('discount_percent, redemption_count, max_redemptions, is_active')
        .eq('event_id', event.id)
        .eq('promo_code', promoCode.trim().toUpperCase())
        .eq('is_active', true);

      if (error || !codes || codes.length === 0) {
        setPromoError(t('registration.dataStep.promo.invalid'));
        setPromoDiscount(null);
        setPromoValidated(false);
        return;
      }

      const code = codes[0];
      if (code.max_redemptions && code.redemption_count >= code.max_redemptions) {
        setPromoError(t('registration.dataStep.promo.expired'));
        setPromoDiscount(null);
        setPromoValidated(false);
        return;
      }

      setPromoDiscount(code.discount_percent);
      setPromoValidated(true);
      setPromoError('');
    } catch {
      setPromoError(t('registration.dataStep.promo.invalid'));
    } finally {
      setPromoLoading(false);
    }
  };

  const removePromoCode = () => {
    setPromoCode('');
    setPromoDiscount(null);
    setPromoValidated(false);
    setPromoError('');
  };

  const calculatePaymentAmount = () => {
    const baseFee = event.entry_fee || 0;
    if (promoValidated && promoDiscount) {
      return parseFloat((baseFee * (1 - promoDiscount / 100)).toFixed(2));
    }
    return baseFee;
  };

  const handleContinueToPayment = () => {
    if (!isFormValid()) return;
    const formData = {
      modality: selectedModality,
      category: selectedCategory,
      subcategory: selectedSubcategory,
      player1Name: player1Name.trim(),
      player2Name: player2Name.trim(),
      partnerSelectionMethod,
      selectedFriend,
      paymentMode: (partnerSelectionMethod === 'friend' && selectedFriend) ? (data.paymentMode || 'full_amount') : 'full_amount',
      promoCode: promoValidated ? promoCode.trim().toUpperCase() : undefined,
      discountPercent: promoValidated ? promoDiscount : undefined,
    };
    onDataChange(formData);
    localStorage.setItem('pendingRegistrationData', JSON.stringify(formData));
    onNext();
  };

  return (
    <div className={`${isMobile ? 'space-y-4' : 'space-y-6'}`}>
      {/* Header */}
      <div className={`text-center ${isMobile ? 'mb-4' : 'mb-6'}`}>
        <h2 className={`${isMobile ? 'text-xl' : 'text-2xl'} font-bold text-gray-900 mb-2`}>
          {t('registration.dataStep.title')}
        </h2>
        <p className={`text-gray-600 ${isMobile ? 'text-sm' : 'text-lg'}`}>
          {isLeague ? t('registration.dataStep.descriptionLeague') : t('registration.dataStep.descriptionTournament')}
        </p>
      </div>

      {/* Modality Selection */}
      <Card className={isMobile ? 'shadow-sm' : ''}>
        <CardHeader className={isMobile ? 'pb-3 px-4 pt-4' : ''}>
          <CardTitle className={`flex items-center gap-2 ${isMobile ? 'text-base' : ''}`}>
            <Users className={`${isMobile ? 'h-4 w-4' : 'h-5 w-5'}`} />
            {t('registration.dataStep.modality')}
          </CardTitle>
        </CardHeader>
        <CardContent className={isMobile ? 'px-4 pb-4' : ''}>
          {availableModalities.length === 1 ? (
            <div className="space-y-3">
              <div className={`flex items-center gap-3 p-3 bg-primary/5 border border-primary/20 rounded-lg ${isMobile ? 'p-2' : ''}`}>
                {availableModalities[0] === 'singles' ? (
                  <>
                    <User className={`${isMobile ? 'h-4 w-4' : 'h-5 w-5'} text-primary`} />
                    <div>
                      <div className={`font-medium text-primary ${isMobile ? 'text-sm' : ''}`}>
                        {t('registration.dataStep.singlesLabel')}
                      </div>
                      <div className={`text-primary/80 ${isMobile ? 'text-xs' : 'text-sm'}`}>
                        {isLeague ? t('registration.dataStep.singlesOnlyLeague') : t('registration.dataStep.singlesOnlyTournament')}
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <Users className={`${isMobile ? 'h-4 w-4' : 'h-5 w-5'} text-primary`} />
                    <div>
                      <div className={`font-medium text-primary ${isMobile ? 'text-sm' : ''}`}>
                        {t('registration.dataStep.doublesLabel')}
                      </div>
                      <div className={`text-primary/80 ${isMobile ? 'text-xs' : 'text-sm'}`}>
                        {isLeague ? t('registration.dataStep.doublesOnlyLeague') : t('registration.dataStep.doublesOnlyTournament')}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          ) : (
            <RadioGroup value={selectedModality} onValueChange={setSelectedModality}>
              <div className={`${isMobile ? 'space-y-3' : 'space-y-2'}`}>
                {availableModalities.map((modality) => (
                  <div key={modality} className={`flex items-center space-x-2 ${isMobile ? 'py-1' : ''}`}>
                    <RadioGroupItem value={modality} id={modality} className={isMobile ? 'h-4 w-4' : ''} />
                    <Label htmlFor={modality} className={`flex items-center gap-2 cursor-pointer ${isMobile ? 'text-sm' : ''}`}>
                      {modality === 'singles' ? (
                        <>
                          <User className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4'}`} />
                          {t('registration.dataStep.singlesLabel')}
                        </>
                      ) : (
                        <>
                          <Users className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4'}`} />
                          {t('registration.dataStep.doublesLabel')}
                        </>
                      )}
                    </Label>
                  </div>
                ))}
              </div>
            </RadioGroup>
          )}
        </CardContent>
      </Card>

      {/* Category Selection */}
      <Card className={isMobile ? 'shadow-sm' : ''}>
        <CardHeader className={isMobile ? 'pb-3 px-4 pt-4' : ''}>
          <CardTitle className={`flex items-center gap-2 ${isMobile ? 'text-base' : ''}`}>
            <Trophy className={`${isMobile ? 'h-4 w-4' : 'h-5 w-5'}`} />
            {t('registration.dataStep.category')}
          </CardTitle>
        </CardHeader>
        <CardContent className={isMobile ? 'px-4 pb-4' : ''}>
          {(event.genderCategories?.length === 1 || 
            (event.gender_category && (!event.genderCategories || event.genderCategories.length === 0))) ? (
            <div className={`flex items-center gap-3 p-3 bg-primary/5 border border-primary/20 rounded-lg ${isMobile ? 'p-2' : ''}`}>
              <Trophy className={`${isMobile ? 'h-4 w-4' : 'h-5 w-5'} text-primary`} />
              <div>
                <div className={`font-medium text-primary ${isMobile ? 'text-sm' : ''}`}>
                  {translateCategory(event.genderCategories?.[0] || event.gender_category || event.category)}
                </div>
                <div className={`text-primary/80 ${isMobile ? 'text-xs' : 'text-sm'}`}>
                  {isLeague ? t('registration.dataStep.categoryOfLeague') : t('registration.dataStep.categoryOfTournament')}
                </div>
              </div>
            </div>
          ) : (
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className={isMobile ? 'h-10 text-sm' : ''}>
                <SelectValue placeholder={t('registration.dataStep.selectCategory')} />
              </SelectTrigger>
              <SelectContent className="z-50 bg-white">
                {event.genderCategories?.map((category: string) => (
                  <SelectItem key={category} value={category} className={isMobile ? 'text-sm py-2' : ''}>
                    {translateCategory(category)}
                  </SelectItem>
                )) || event.gender_category && (
                  <SelectItem key={event.gender_category} value={event.gender_category}>
                    {translateCategory(event.gender_category)}
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
          )}
        </CardContent>
      </Card>

      {/* Subcategory Selection */}
      {selectedCategory && availableSubcategories.length > 0 && (
        <Card className={isMobile ? 'shadow-sm' : ''}>
          <CardHeader className={isMobile ? 'pb-3 px-4 pt-4' : ''}>
            <CardTitle className={`flex items-center gap-2 ${isMobile ? 'text-base' : ''}`}>
              <Trophy className={`${isMobile ? 'h-4 w-4' : 'h-5 w-5'}`} />
              {t('registration.dataStep.subcategory')}
            </CardTitle>
          </CardHeader>
          <CardContent className={isMobile ? 'px-4 pb-4' : ''}>
            {availableSubcategories.length === 1 ? (
              <div className={`flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg ${isMobile ? 'p-2' : ''}`}>
                <Trophy className={`${isMobile ? 'h-4 w-4' : 'h-5 w-5'} text-green-600`} />
                <div>
                  <div className={`font-medium text-green-900 ${isMobile ? 'text-sm' : ''}`}>
                    {translateSubcategory(availableSubcategories[0])}
                  </div>
                  <div className={`text-green-700 ${isMobile ? 'text-xs' : 'text-sm'}`}>
                    {isLeague ? t('registration.dataStep.subcategoryOfLeague') : t('registration.dataStep.subcategoryOfTournament')}
                  </div>
                </div>
              </div>
            ) : (
              <>
                <Select value={selectedSubcategory} onValueChange={(value) => {
                  const info = subcategoryCounts[value];
                  if (!info?.isFull) {
                    setSelectedSubcategory(value);
                  }
                }}>
                  <SelectTrigger className={isMobile ? 'h-10 text-sm' : ''}>
                    <SelectValue placeholder={t('registration.dataStep.selectSubcategory')} />
                  </SelectTrigger>
                  <SelectContent className="z-50 bg-white">
                    {availableSubcategories.map((subcategory: string) => {
                      const info = subcategoryCounts[subcategory];
                      const isFull = info?.isFull || false;
                      const currentPairs = info?.pairsCount || 0;
                      const max = info?.max;
                      const remaining = info?.remaining;
                      
                      return (
                        <SelectItem 
                          key={subcategory} 
                          value={subcategory}
                          disabled={isFull}
                          className={`${isMobile ? 'text-sm py-3' : 'py-3'} ${
                            isFull ? 'opacity-50 cursor-not-allowed' : ''
                          } data-[state=checked]:bg-green-500 data-[state=checked]:text-white`}
                        >
                          <div className="flex items-center gap-2 w-full">
                            <span className={`font-semibold ${isFull ? 'line-through' : ''}`}>
                              {translateSubcategory(subcategory)}
                            </span>
                            {max !== null ? (
                              <Badge 
                                variant={isFull ? "destructive" : "default"}
                                className={`ml-auto text-xs px-2 py-0.5 ${
                                  isFull 
                                    ? 'bg-red-100 text-red-700 hover:bg-red-100' 
                                    : 'bg-green-500 text-white hover:bg-green-500'
                                }`}
                              >
                                {isFull ? t('registration.dataStep.full') : `${remaining} ${t('registration.dataStep.available')}`}
                              </Badge>
                            ) : (
                              <Badge className="ml-auto text-xs px-2 py-0.5 bg-blue-100 text-blue-700 hover:bg-blue-100">
                                {currentPairs} {currentPairs === 1 ? t('registration.dataStep.enrolled') : t('registration.dataStep.enrolledPlural')}
                              </Badge>
                            )}
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                
                {/* Selected subcategory summary */}
                {selectedSubcategory && subcategoryMax !== null && (
                  <div className={`mt-3 text-sm ${
                    remainingPairs !== null && remainingPairs <= 3 
                      ? 'text-orange-600 font-medium' 
                      : 'text-muted-foreground'
                  }`}>
                    {allSubcategoriesLoading ? (
                      t('registration.dataStep.loadingAvailability')
                    ) : remainingPairs !== null ? (
                      remainingPairs > 0 ? (
                        <>
                          {t('registration.dataStep.spotsAvailable')} {translateCategory(selectedCategory)} {translateSubcategory(selectedSubcategory)}:{' '}
                          <strong>{remainingPairs} {remainingPairs === 1 ? t('registration.dataStep.pair') : t('registration.dataStep.pairs')}</strong>
                          {remainingPairs <= 3 && ` ⚠️ ${t('registration.dataStep.lastSpots')}`}
                        </>
                      ) : (
                        <span className="text-destructive font-medium">
                          ❌ {t('registration.dataStep.spotsFull')} {translateCategory(selectedCategory)} {translateSubcategory(selectedSubcategory)}
                        </span>
                      )
                    ) : null}
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Player Names */}
      <Card className={isMobile ? 'shadow-sm' : ''}>
        <CardHeader className={isMobile ? 'pb-3 px-4 pt-4' : ''}>
          <CardTitle className={`flex items-center gap-2 ${isMobile ? 'text-base' : ''}`}>
            <User className={`${isMobile ? 'h-4 w-4' : 'h-5 w-5'}`} />
            {isDoublesRequired ? t('registration.dataStep.playerNames') : t('registration.dataStep.playerName')}
          </CardTitle>
        </CardHeader>
        <CardContent className={`${isMobile ? 'px-4 pb-4 space-y-3' : 'space-y-4'}`}>
          <div>
            <Label htmlFor="player1" className={isMobile ? 'text-xs font-medium' : ''}>
              {isDoublesRequired ? t('registration.dataStep.player1') : t('registration.dataStep.fullName')}
            </Label>
            <Input
              id="player1"
              value={player1Name}
              onChange={(e) => setPlayer1Name(e.target.value)}
              placeholder={t('registration.dataStep.enterFullName')}
              className={`mt-1 ${isMobile ? 'h-10 text-sm' : ''}`}
            />
          </div>

          {isDoublesRequired && (
            <>
              {/* Partner Selection Method */}
              <div className={isMobile ? 'mt-3' : 'mt-4'}>
                <Label className={`${isMobile ? 'text-xs' : 'text-sm'} font-medium mb-2 block`}>
                  {t('registration.dataStep.howAddPartner')}
                </Label>
                <RadioGroup value={partnerSelectionMethod} onValueChange={setPartnerSelectionMethod}>
                  <div className={`${isMobile ? 'space-y-2' : 'space-y-3'}`}>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="manual" id="manual" />
                      <Label htmlFor="manual" className="cursor-pointer text-sm">
                        {t('registration.dataStep.manualEntry')}
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="friend" id="friend" />
                      <Label htmlFor="friend" className="cursor-pointer text-sm">
                        {t('registration.dataStep.selectFromFriends')}
                      </Label>
                    </div>
                  </div>
                </RadioGroup>
              </div>

              {/* Manual Partner Name Input */}
              {partnerSelectionMethod === 'manual' && (
                <div className={isMobile ? 'mt-3' : 'mt-4'}>
                  <Label htmlFor="player2" className={isMobile ? 'text-xs font-medium' : ''}>
                    {t('registration.dataStep.partnerName')}
                  </Label>
                  <Input
                    id="player2"
                    value={player2Name}
                    onChange={(e) => setPlayer2Name(e.target.value)}
                    placeholder={t('registration.dataStep.enterPartnerName')}
                    className={`mt-1 ${isMobile ? 'h-10 text-sm' : ''}`}
                  />
                  <p className={`text-xs text-muted-foreground mt-1 ${isMobile ? 'text-xs' : ''}`}>
                    {t('registration.dataStep.payBothNote')}
                  </p>
                </div>
              )}

              {/* Friend Selection */}
              {partnerSelectionMethod === 'friend' && (
                <div className={isMobile ? 'mt-3' : 'mt-4'}>
                  <Label className={`${isMobile ? 'text-xs' : 'text-sm'} font-medium mb-2 block`}>
                    {t('registration.dataStep.searchFriend')}
                  </Label>
                  <FriendSelector
                    onSelectFriend={setSelectedFriend}
                    selectedFriend={selectedFriend}
                    eventSport={event.sport || ''}
                  />
                </div>
              )}

              {/* Payment Mode Selection - When friend is selected */}
              {partnerSelectionMethod === 'friend' && selectedFriend && (
                <div className={isMobile ? 'mt-3' : 'mt-4'}>
                  <Label className={`${isMobile ? 'text-xs' : 'text-sm'} font-medium mb-2 block`}>
                    {t('registration.dataStep.paymentModeLabel')}
                  </Label>
                  <RadioGroup 
                    value={data.paymentMode || 'full_amount'} 
                    onValueChange={(value) => onDataChange({ ...data, paymentMode: value })}
                  >
                    <div className={`${isMobile ? 'space-y-2' : 'space-y-3'}`}>
                      <div className={`border rounded-lg p-3 cursor-pointer transition-colors ${
                        (data.paymentMode || 'full_amount') === 'full_amount' ? 'border-primary bg-primary/5' : 'border-border'
                      }`}>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="full_amount" id="full_amount" />
                          <Label htmlFor="full_amount" className="cursor-pointer text-sm font-medium">
                            {t('registration.dataStep.fullPayment')}
                          </Label>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1 ml-6">
                          {t('registration.dataStep.fullPaymentDesc')} {selectedFriend?.full_name || selectedFriend?.email || t('registration.dataStep.partnerDefault')}
                        </p>
                      </div>
                      
                      {(() => {
                        const eventStartDate = new Date(event.start_date);
                        const now = new Date();
                        const hoursUntilEvent = (eventStartDate.getTime() - now.getTime()) / (1000 * 60 * 60);
                        const splitAvailable = hoursUntilEvent >= 2;
                        const splitHours = Math.min(10, Math.floor(hoursUntilEvent - 1));
                        
                        return (
                          <div className={`border rounded-lg p-3 transition-colors ${
                            !splitAvailable ? 'opacity-50 cursor-not-allowed border-border' :
                            data.paymentMode === 'split' ? 'border-primary bg-primary/5 cursor-pointer' : 'border-border cursor-pointer'
                          }`}>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="split" id="split" disabled={!splitAvailable} />
                              <Label htmlFor="split" className={`text-sm font-medium ${!splitAvailable ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                                {t('registration.dataStep.splitPaymentLabel')} — ${(calculatePaymentAmount() / 2).toFixed(2)} {t('registration.dataStep.each')}
                              </Label>
                            </div>
                            <p className="text-xs text-muted-foreground mt-1 ml-6">
                              {splitAvailable 
                                ? `${t('registration.dataStep.splitPaymentDesc')} ${selectedFriend?.full_name || t('registration.dataStep.partnerDefault')} ${t('registration.dataStep.splitPaymentHours').replace('{hours}', splitHours.toString())}`
                                : t('registration.dataStep.splitNotAvailable')
                              }
                            </p>
                          </div>
                        );
                      })()}
                    </div>
                  </RadioGroup>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>


      {/* Promo Code */}
      {(event.entry_fee || 0) > 0 && hasPromoCodes && (
        <Card className={isMobile ? 'shadow-sm' : ''}>
          <CardHeader className={isMobile ? 'pb-3 px-4 pt-4' : ''}>
            <CardTitle className={`flex items-center gap-2 ${isMobile ? 'text-base' : ''}`}>
              <Tag className={`${isMobile ? 'h-4 w-4' : 'h-5 w-5'}`} />
              {t('registration.dataStep.promo.title')}
            </CardTitle>
          </CardHeader>
          <CardContent className={isMobile ? 'px-4 pb-4' : ''}>
            {promoValidated && promoDiscount ? (
              <div className="space-y-2">
                <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-600" />
                    <span className="text-sm font-medium text-green-800">
                      {t('registration.dataStep.promo.valid')} {promoDiscount}%
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={removePromoCode}
                    className="h-7 px-2 text-muted-foreground hover:text-destructive"
                  >
                    <X className="h-3 w-3 mr-1" />
                    {t('registration.dataStep.promo.remove')}
                  </Button>
                </div>
                <p className="text-sm text-green-700">
                  {t('registration.dataStep.promo.savings')}: ${((event.entry_fee || 0) * promoDiscount / 100).toFixed(2)} MXN
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex gap-2">
                  <Input
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                    placeholder={t('registration.dataStep.promo.placeholder')}
                    className={`flex-1 ${isMobile ? 'h-10 text-sm' : ''}`}
                    disabled={promoLoading}
                  />
                  <Button
                    onClick={validatePromoCode}
                    disabled={promoLoading || !promoCode.trim()}
                    variant="outline"
                    className={isMobile ? 'h-10 text-sm' : ''}
                  >
                    {promoLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-1" />
                        {t('registration.dataStep.promo.applying')}
                      </>
                    ) : (
                      t('registration.dataStep.promo.apply')
                    )}
                  </Button>
                </div>
                {promoError && (
                  <p className="text-sm text-destructive">{promoError}</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Continue Button */}
      <div className={`${isMobile ? 'flex gap-2 pt-2' : 'flex gap-3 pt-4'} border-t`}>
        {onPrevious && (
          <Button 
            onClick={onPrevious}
            variant="outline"
            className={`flex-1 ${isMobile ? 'h-11 text-sm' : ''}`}
          >
            {t('registration.dataStep.back')}
          </Button>
        )}
        <Button
          onClick={handleContinueToPayment}
          disabled={!isFormValid() || (subcategoryMax !== null && pairsCount >= subcategoryMax)}
          className={`${onPrevious ? 'flex-1' : 'w-full'} bg-green-600 hover:bg-green-700 text-white ${isMobile ? 'h-11 text-sm font-medium' : ''}`}
        >
          {subcategoryMax && pairsCount >= subcategoryMax ? (
            t('registration.dataStep.spotsExhausted')
          ) : (
            `${t('registration.dataStep.continue')} - $${calculatePaymentAmount().toFixed(2)}`
          )}
        </Button>
      </div>
    </div>
  );
};
